import { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import {
    getMultiFactorResolver,
    type MultiFactorError,
    PhoneMultiFactorGenerator,
    TotpSecret,
    type User,
    TotpMultiFactorGenerator,
    getAuth,
    multiFactor,
    type MultiFactorInfo,
    EmailAuthProvider,
    reauthenticateWithCredential,
    updatePassword,
} from 'firebase/auth';
import storage from 'redux-persist/lib/storage';

import { clearToken, clearUser, setToken } from './authSlice';
import { toggleToast } from '../../common/application/toastSlice';
import { auth } from '../../common/data/firebase/firebase';
import { useTranslatedValue } from '../../common/presentation/TranslatedValue.tsx';
import { apiCheckAuthState, apiLoginUser, apiLoginWithRedirect, apiLogoutUser, apiRegisterUser } from '../data/authApi';
import { validateNewPassword } from '../domain/auth.ts';

import { type RootState } from '#root//rootState';
import { clearContext } from '@/common/application/contextSlice';
import { setParkingAdmins, setParkstoryAdmins } from '@/common/application/contextSlice.ts';
import { RequestTypeEnum } from '@/common/domain/toast.ts';
import { logException, logSecurityEvent, SeverityLevel } from '@/common/utils/logger';
import { useGroup } from '@/group/application/groupController';
import { clearStore } from '@/group/data/groupStore.ts';

import { clearPendingUser } from '@/user/application/pendingUserSlice';
import { useUser } from '@/user/application/userController';


let isFetchingAdmins = false;

export const useAuth = () => {
    const dispatch = useDispatch();
    const t = useTranslatedValue();
    const { getUserProfile } = useUser();
    const currToken = useSelector((state: RootState) => state.auth.token);

    const currAdmins = useSelector((state: RootState) => state.context.parkingAdmins);
    const { getGroupsAdmin, getParkingAdmins } = useGroup();
    const user = auth.currentUser;
    if (!user) { 
        /*
         *  throw Error('noUserOrWrongUser');
         * should logout ?
         * TODO
         */
    }

    // Return a list of enrolled factors for user to select
    const get2FAenrolledFactors = (error: MultiFactorError) => {
        const mfaResolver = getMultiFactorResolver(getAuth(), error);
        const enrolledFactors = mfaResolver.hints.map((info: any) => info.displayName);
        throw {content: enrolledFactors, message: 'enrolledFactors', mfaError: error};
    };

    // Log in user
    const loginUser = useCallback(async (email: string, password: string) : Promise<{userProfile: any, user: User | firebase.default.User} | undefined> => {
        try {
            const { token, user } = await apiLoginUser(email, password);
            if (!user) { 
                return;
            }
            if (token) {
                dispatch(setToken(token));
                const userProfile = await getUserProfile();
                return { user: user, userProfile: userProfile};
            }
        } catch (error: any) {
            logException(error, { code: error.code });

            dispatch(clearUser());
            dispatch(clearToken());
            if (error.message == 'authentication.emailNotVerified') {
                return Promise.reject(new Error(t('authentication.error.emailNotVerified')));
            }
            switch (error.code) {
                case 'auth/invalid-credential':
                    logSecurityEvent('AuthInvalidCredential', SeverityLevel.Warning, { code: error.code });
                    return Promise.reject(new Error(t('authentication.error.invalidCredential')));
                case 'auth/too-many-requests':
                    logSecurityEvent('AuthBruteForceSuspected', SeverityLevel.Critical, { code: error.code });
                    return Promise.reject(new Error(t('error.tooManyRequests')));
                case 'auth/multi-factor-auth-required':
                    get2FAenrolledFactors(error);
                    break;
                default:
                    return Promise.reject(new Error(t('authentication.error.default')));
            }
        }
    }, []);

    // Log out user
    const logoutUser = useCallback(async (options?: { showSuccessToast?: boolean; suppressErrorToast?: boolean }) => {
        try {
            await apiLogoutUser();
            dispatch(clearStore());
            dispatch(clearUser());
            dispatch(clearToken());
            dispatch(clearContext());
            storage.removeItem('accessToken');
            sessionStorage.removeItem('accessToken');
            storage.removeItem('pksCtx');

            if (options?.showSuccessToast) {
                dispatch(toggleToast({
                    code: 200,
                    requestType: RequestTypeEnum.logoutUser,
                    text: ''
                }));
            }
        } catch (error: any) {
            // Internal cleanup logouts (e.g. during registration) opt out of the error toast.
            if (!options?.suppressErrorToast) {
                dispatch(toggleToast({...error?.status, requestType: RequestTypeEnum.logoutUser }));
            }
        }
    }, []);

    // Enroll user in 2FA: get the secretKey to enter in 2FA app for enrolling in the first place
    const generateTOTPsecret = async() => {
        if (!user) { 
            throw Error('noUserOrWrongUser');
        }
        try {
            // Generate a TOTP secret.
            const multiFactorSession = await multiFactor(user).getSession();
            const totpSecret = await TotpMultiFactorGenerator.generateSecret(
                multiFactorSession
            );
            return totpSecret;
        } catch (error) {
            logException(error);
            throw error;
        }
    };

    // Get auth factors
    const get2FAfactors = async () : Promise<MultiFactorInfo[]> => {
        if (!user) {
            return [];
        }
        try {
            const options = user.multiFactor.enrolledFactors;
            return options;
        } catch (error) {
            logException(error);
            throw error;
        }
    };

    const editPassword = async(newPassword: string) : Promise<void> => {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
            return;
        }
        try {
            validateNewPassword(newPassword);
            await updatePassword(user, newPassword);
            dispatch(toggleToast({ code: 200, requestType: RequestTypeEnum.editPassword, text: t('success.editPassword') }));
        } catch (error: any) {
            logException(error);
            throw error;
        }
    };

    // Sign in with OTP from 2FA factor
    const signInWith2FA = async(selectedIndex: number, OTP: string, mfaError: MultiFactorError) => {
        const mfaResolver = getMultiFactorResolver(getAuth(), mfaError);
        switch (mfaResolver.hints[selectedIndex].factorId) {
            case TotpMultiFactorGenerator.FACTOR_ID:
                {
                    const otpFromAuthenticator = OTP; // OTP typed by the user.
                    const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(mfaResolver.hints[selectedIndex].uid, otpFromAuthenticator);
                    try {
                        const userCredential = await mfaResolver.resolveSignIn(
                            multiFactorAssertion
                        );

                        // set token
                        const accessToken = await userCredential.user?.getIdToken(true) as string;
                        if (accessToken) {
                            dispatch(setToken(accessToken));
                            const userProfile = await getUserProfile();
                            return { user: userCredential.user, userProfile: userProfile};
                        }
                    } catch (error : any) {
                        logException(error, { code: error.code });
                        if (error.code.includes('auth/totp-challenge-timeout')) {
                            dispatch(toggleToast({code: -1, text: t('error.expiredSession') }));
                            return Promise.reject(error);
                        }
                        if (error.code.includes('auth/invalid-verification-code')) {
                            return Promise.reject(error);
                        }
                        if (error.code.includes('auth/network-request-failed')) {
                            dispatch(toggleToast({...error?.status, requestType: RequestTypeEnum.loginWith2FA }));
                            return Promise.reject(error);
                        }
                        if (error?.status) {
                            dispatch(toggleToast({...error?.status, requestType: RequestTypeEnum.loginWith2FA }));
                        }
                        throw error;
                    }}
                break;
            case PhoneMultiFactorGenerator.FACTOR_ID:
                /*
                 * Handle SMS second factor.
                 * TODO ?
                 */
                break;
            default:
                // Unsupported second factor?
                break;
        } 
    };

    // Re authenticate user
    const reAuthenticate = async (email: string, password: string, OTP?: string) => {
        if (!user) {
            loginUser(email, password);
            return [];
        }
        try {
            const credential = EmailAuthProvider.credential(email, password);
            await reauthenticateWithCredential(
                user,
                credential
            );
        } catch (error: any) {
            logException(error, { code: error.code });
            const errorCode = error.code;

            switch (errorCode) {
                case 'auth/invalid-credential':
                    logSecurityEvent('AuthInvalidCredential', SeverityLevel.Warning, { code: errorCode });
                    if (OTP) {
                        return Promise.reject(new Error(t('authentication.error.invalidPwdOrOTP')));
                    } else {
                        return Promise.reject(new Error(t('authentication.error.invalidCredential')));
                    }
                case 'auth/too-many-requests':
                    logSecurityEvent('AuthBruteForceSuspected', SeverityLevel.Critical, { code: errorCode });
                    return Promise.reject(new Error(t('error.tooManyRequests')));
                case 'auth/multi-factor-auth-required':
                    if (OTP) {
                        signInWith2FA(0, OTP, error as MultiFactorError);
                    } else {
                        // user session might be unsync (OTP set up in a parallel session) => need to relog
                        dispatch(toggleToast({...error?.status, requestType: RequestTypeEnum.expiredSession }));
                        logoutUser();
                    }
                    break; 
                default:
                    return Promise.reject(new Error(t('authentication.error.default')));
            }
        }
    };

    const unEnrollFromFactor = async(mfaFactorOption: MultiFactorInfo | string, email?: string, password?: string, OTP?: string) : Promise<boolean> => {
        try {
            if (!user || !mfaFactorOption) { 
                return false;
            }

            // Unenroll from TOTP MFA.
            await multiFactor(user).unenroll(mfaFactorOption);
            dispatch(toggleToast({
                code: 200,
                requestType: RequestTypeEnum.remove2FAfactor,
                text: ''
            }));
            return true;

        } catch (error : any) {
            dispatch(toggleToast({...error?.status, requestType: RequestTypeEnum.remove2FAfactor }));

            if (error.code === 'auth/user-token-expired' || error.message == 'TOKEN_EXPIRED') {
                // If the user was signed out, re-authenticate them.
                if (email && password) {
                    if (OTP) {
                        reAuthenticate(email, password, OTP);
                    } else {
                        reAuthenticate(email, password);
                    }
                } 
            }
            throw error;
        }
    };

    
    // Enroll a second auth factor
    const enrollMFAfactor = async (verificationCode: string, totpSecret: TotpSecret, factorDisplayName: string) : Promise<boolean> => {
        try {
            const user = auth.currentUser;
            if (!user) {
                throw Error;
            }

            // Finalize the enrollment.
            const multiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(
                totpSecret,
                verificationCode
            );

            if (user) {
                await multiFactor(user).enroll(multiFactorAssertion, factorDisplayName); 
            }
            dispatch(toggleToast({
                code: 200,
                requestType: RequestTypeEnum.add2FAfactor,
                text: ''
            }));
            return true;

        } catch (error : any) {
            logException(error, { code: error.code });
            switch (error.code) {
                case 'auth/invalid-verification-code':
                    return Promise.reject(error);
                case 'auth/second-factor-already-in-use':
                    dispatch(toggleToast({...error?.status, requestType: RequestTypeEnum.add2FAfactor, text: t('error.secondFactorAlreadyUsed') }));
                    break;
                case 'auth/maximum-second-factor-count-exceeded':
                    dispatch(toggleToast({code: -1, requestType: RequestTypeEnum.add2FAfactor, text: t('error.alreadyHasSecondFactor') }));
                    break;
                default:
                    return Promise.reject(new Error(error.message));
            } 
        }
    };
  

    const createUser = async (user: firebase.default.User) => {
        if (!user.uid) {
            throw Error('UserUIDRequired'); 
        }
        try {
            const userProfileInfo = await getUserProfile();
            return userProfileInfo;
        } catch (error: any) {
            if (error?.status) {
                dispatch(toggleToast({...error?.status, requestType: RequestTypeEnum.createUser }));
            } else {
                logException(error);
            }
        }
    };
    

    const checkAuthState = useCallback(async () => {
        try {
            const token = await apiCheckAuthState();
            if (!currAdmins && !isFetchingAdmins) {
                try {
                    isFetchingAdmins = true;
                    const parkstoryAdmins = await getGroupsAdmin();
                    const parkingAdmins = await getParkingAdmins();
                    dispatch(setParkstoryAdmins(parkstoryAdmins));
                    dispatch(setParkingAdmins(parkingAdmins));
                } catch (e) {
                    logException(e);
                } finally {
                    isFetchingAdmins = false;
                }
            }
            if (typeof token != 'undefined' && token !== null && token !== currToken) {
                dispatch(setToken(token));
                await getUserProfile();
            }

        } catch (error : any) {
            dispatch(clearUser());
            dispatch(clearPendingUser());
            dispatch(clearToken());
            if (error?.status) {
                dispatch(toggleToast({...error?.status, requestType: RequestTypeEnum.unknown }));
            }
            return false;
        }
    }, []);
    
    const registerUser = useCallback(async (email: string, password: string) => {
        try {
            validateNewPassword(password);
            await apiRegisterUser(email, password);
            // Cleanup logout after registration — no logout error toast, no lingering role
            // (which would otherwise surface the profile chip on the auth screen).
            await logoutUser({ suppressErrorToast: true });
        } catch (error: any) {
            dispatch(clearUser());
            dispatch(clearPendingUser());
            dispatch(clearContext());
            throw error;
        }
    }, [dispatch]);

    const sendEmailPasswordResetAction = useCallback(async (email: string) => {
        try {
            await auth.sendPasswordResetEmail(email);
        } catch (error: any) {
            logException(error);
            dispatch(toggleToast({ ...error.status, requestType: RequestTypeEnum.sendEmailPasswordResetAction }));
        }
    }, []);

    const loginWithRedirect = useCallback(async (provider: string) => {
        try {
            const { token } = await apiLoginWithRedirect(provider);
            dispatch(setToken(token));
            await getUserProfile(); // state user and pendingUser are set in getUserProfile
        } catch (error: any) {
            dispatch(clearUser());
            dispatch(toggleToast({ ...error.status, requestType: RequestTypeEnum.loginWithRedirect }));
        }
    }, []);

    // const getCurrentUser = useCallback(() => auth.currentUser, []);

    const verifyEmail = useCallback(async (oobCode: string) => {
        try {
            await auth.applyActionCode(oobCode);
            await logoutUser();
            return Promise.resolve();
        } catch (error: any) {
            dispatch(toggleToast({ ...error.status, requestType: RequestTypeEnum.verifyEmail }));
            return Promise.reject(error);
        }
    }, []);

    const verifyPasswordResetCode = useCallback(async (oobCode: string) => {
        try {
            const email = await auth.verifyPasswordResetCode(oobCode);
            return Promise.resolve(email);
        } catch (error) {
            return Promise.reject(error);
        }
    }, []);

    const confirmPasswordReset = useCallback(async (oobCode: string, newPassword: string) => {
        try {
            validateNewPassword(newPassword);            
            await auth.confirmPasswordReset(oobCode, newPassword);
            await logoutUser();

            return Promise.resolve();
        } catch (error: any) {
            const errorCode = error.code;
            const errorMessage = error.message;
            switch (errorCode) {
                case 'auth/expired-action-code':
                    return Promise.reject(new Error(t('authentication.resetPassword.error.expiredActionCode')));
                case 'auth/invalid-action-code':
                    return Promise.reject(new Error(t('authentication.resetPassword.error.invalidActionCode')));
                case 'auth/user-disabled':
                    return Promise.reject(new Error(t('authentication.resetPassword.error.userDisabled')));
                case 'auth/user-not-found':
                    return Promise.reject(new Error(t('authentication.resetPassword.error.userNotFound')));
                case 'auth/weak-password':
                    return Promise.reject(new Error(t('authentication.resetPassword.error.weakPassword')));
                default:
                    return Promise.reject(new Error(errorMessage));
            }
        }
    }, []);

    return {
        checkAuthState,
        confirmPasswordReset,
        createUser,
        editPassword,
        enrollMFAfactor,
        generateTOTPsecret,
        get2FAenrolledFactors,
        get2FAfactors,
        loginUser,
        loginWithRedirect,
        logoutUser,
        reAuthenticate,
        registerUser,
        sendEmailPasswordResetAction,
        signInWith2FA,
        unEnrollFromFactor,
        verifyEmail,
        verifyPasswordResetCode
    };
};
